import { useState, useEffect, useCallback } from "react";
import styled, { keyframes } from "styled-components";
import { eraseApiCall, logoutApiCall } from "./Api.js";
import FormSignin from "./auth/FormSignin.js";
import FormSignup from "./auth/FormSignup.js";
import FormUpdate from "./auth/FormUpdate.js";
import FormAuthenticated from "./auth/FormAuthenticated.js";
import toast from "react-hot-toast";
import { t } from "@lingui/core/macro";

const jump = keyframes`
  from{
    transform: translateY(0)
  }
  to{
    transform: translateY(-3px)
  }
`;

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
`;

const Button = styled.button`
  max-width: 100%;
  min-width: 50%;
  padding: 11px 13px;
  color: rgb(253, 249, 243);
  font-weight: 600;
  text-transform: uppercase;
  background: #f03d4e;
  border: none;
  border-radius: 3px;
  outline: 0;
  cursor: pointer;
  margin-top: 0.6rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease-out;
  &:hover {
    background-color: rgb(93, 25, 34);
    animation: ${jump} 0.2s ease-out forwards;
  }
`;

const SignupLink = styled.a`
  font-weight: normal;
  color: rgb(219, 219, 74);
  text-shadow: 0 1px 3px rgba(142, 117, 8, 0.1), 0 1px 2px rgba(0, 0, 0, 0.1);
  text-align: center;
  cursor: pointer;
`;

/**
 * A component that displays an authentication form
 *
 * @param {object} props
 * @returns {JSX.Element}
 */
const FormAuth = (options) => {
  const [formStatus, setFormStatus] = useState("signin");
  const [isAuthenticated, setIsAuthenticated] = useState(
    localStorage.getItem("isAuthenticated") === "true"
  );

  // 🔥 Save authentication state to localStorage when it changes
  useEffect(() => {
    localStorage.setItem("isAuthenticated", isAuthenticated);
    if (isAuthenticated) {
      setFormStatus("authenticated");
    }
  }, [isAuthenticated]);
  // Check if local storage is valorized and set variable of state
  useEffect(() => {
    const isLoggedIn = JSON.parse(localStorage.getItem("isAuthenticated"));
    if (isLoggedIn) {
      setFormStatus("authenticated");
    }
  }, []);

  /**
   * A function that manages a form switch layout
   * @param {string} mode - A layout mode
   * @returns {void} Set a variable of state
   */
  const handleSwitchForm = (mode) => {
    setFormStatus(mode);
  };

  /**
   * A callback to manage authentication status
   * @returns {void} Set a variable of state and call an option function
   */
  const handleSubmission = useCallback(() => {
    const isLoggedIn = JSON.parse(localStorage.getItem("isAuthenticated"));
    if (isLoggedIn) {
      setFormStatus("authenticated");
      options.loggedInOut();
    }
  }, []);

  /**
   * A function that manages account deletion
   * @returns {void} Call an Api for deletion and set variables of state
   */
  const handleDelete = () => {
    if (!window.confirm(t`Are you sure you want to delete your account?`)) {
      return false;
    }
    eraseApiCall()
      .then((data) => {
        console.log(data);
        if (data.success) {
          toast(t`Account deleted successfully!`, {
            icon: "👋",
          });
          setIsAuthenticated(false);
          setFormStatus("signin");
        } else {
          toast(data.error.message || t`Generic delete account error`, {
            icon: "🥵",
          });
        }
      })
      .catch((error) => console.error(error));
  };

  /**
   * A function that manages an account logout
   * @returns {void} Set local storage and set variables of state
   */
  const handleLogout = () => {
    logoutApiCall()
      .then((data) => {
        if (data.success) {
          // Set current login info cookie
          localStorage.setItem("isAuthenticated", false);
          toast(t`Logged out successfully!`, {
            icon: "👋",
          });
          options.loggedInOut();
          setFormStatus("signin");
        } else {
          toast(t`Generic logout error`, {
            icon: "🥵",
          });
        }
      })
      .catch((error) => console.error(error));
  };

  return (
    <>
      {" "}
      {(() => {
        switch (formStatus) {
          case "signin":
            return (
              <>
                <FormSignin handleSubmission={handleSubmission} />
                <SignupLink onClick={() => handleSwitchForm("signup")}>
                  {t`Not registered?`}
                </SignupLink>
                <hr />
                <SignupLink href="/requestresetpwd">
                  {t`Forgot your password?`}
                </SignupLink>
              </>
            );
          case "signup":
            return (
              <>
                <FormSignup handleSubmission={handleSubmission} />
                <SignupLink onClick={() => handleSwitchForm("signin")}>
                  {t`Already registered?`}
                </SignupLink>
              </>
            );
          case "update":
            return (
              <>
                <FormUpdate handleSubmission={handleSubmission} />
                <SignupLink onClick={() => handleSwitchForm("authenticated")}>
                  {t`Cancel`}
                </SignupLink>
              </>
            );
          case "authenticated":
            return (
              <StyledWrapper>
                <FormAuthenticated />
                <Button onClick={() => handleSwitchForm("update")}>
                  ⚙️ {t`Update your details`}
                </Button>
                <Button onClick={() => handleDelete()}>
                  👎 {t`Cancella account`}
                </Button>
                <Button onClick={() => handleLogout()}>🏃‍♂️ {t`Logout`}</Button>
              </StyledWrapper>
            );
          default:
            return null;
        }
      })()}
    </>
  );
};

export default FormAuth;
